import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../context/AuthContext'
import { notifyClubAdmins } from '../../../lib/notificationHelpers'
import { useGeoAge } from '../../../hooks/useGeoAge'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'

// ── Helpers ───────────────────────────────────────────────────────────────────
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
]

function getDaysInMonth(month, year) {
  if (!month) return 31
  if (!year) return new Date(2000, parseInt(month), 0).getDate()
  return new Date(parseInt(year), parseInt(month), 0).getDate()
}

function DOBPicker({ value, onChange, inputClass, labelClass, error }) {
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i)

  const [month, setMonth] = useState('')
  const [day, setDay] = useState('')
  const [year, setYear] = useState('')

  const daysInMonth = getDaysInMonth(month, year)
  const days = Array.from({ length: daysInMonth }, (_, i) => String(i + 1).padStart(2, '0'))

  const notify = (m, d, y) => {
    if (m && d && y) {
      const maxDay = getDaysInMonth(m, y)
      const clampedDay = Math.min(parseInt(d), maxDay)
      onChange(`${y}-${m}-${String(clampedDay).padStart(2, '0')}`)
    } else {
      onChange('')
    }
  }

  const handleMonth = (m) => {
    setMonth(m)
    const max = getDaysInMonth(m, year)
    const clampedDay = day && parseInt(day) > max ? '' : day
    if (clampedDay !== day) setDay(clampedDay)
    notify(m, clampedDay, year)
  }

  const handleDay = (d) => { setDay(d); notify(month, d, year) }
  const handleYear = (y) => {
    setYear(y)
    const max = getDaysInMonth(month, y)
    const clampedDay = day && parseInt(day) > max ? '' : day
    if (clampedDay !== day) setDay(clampedDay)
    notify(month, clampedDay, y)
  }

  const selectClass = `${inputClass} ${error ? 'border-red-400 dark:border-red-500' : ''}`

  return (
    <div className="flex flex-col gap-2">
      <label className={labelClass}>Date of Birth <span className="text-red-500">*</span></label>
      <div className="grid grid-cols-3 gap-2">
        <select value={month} onChange={e => handleMonth(e.target.value)} className={selectClass}>
          <option value="">Month</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>
          ))}
        </select>
        <select value={day} onChange={e => handleDay(e.target.value)} className={selectClass}>
          <option value="">Day</option>
          {days.map(d => <option key={d} value={d}>{parseInt(d)}</option>)}
        </select>
        <select value={year} onChange={e => handleYear(e.target.value)} className={selectClass}>
          <option value="">Year</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  )
}

function PhoneRow({ label, countryValue, numberValue, onCountryChange, onNumberChange, countries, geoLoading, required, helperText, inputClass, labelClass }) {
  const codeSelectClass = "bg-gray-100 dark:bg-gray-900 border border-r-0 border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-3 py-3 text-sm outline-none transition-colors w-32 flex-shrink-0"

  return (
    <div className="flex flex-col gap-2">
      <label className={labelClass}>{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      <div className="flex">
        {geoLoading ? (
          <div className={`${codeSelectClass} flex items-center text-gray-400 dark:text-gray-500 cursor-not-allowed`}>
            <span className="text-xs">Detecting…</span>
          </div>
        ) : (
          <select
            value={countryValue}
            onChange={e => onCountryChange(e.target.value)}
            required={required}
            className={codeSelectClass}
          >
            <option value="">+ Code</option>
            {countries.map(c => (
              <option key={c.country_code} value={c.country_code}>
                {c.dial_code} ({c.country_code})
              </option>
            ))}
          </select>
        )}
        <input
          type="tel"
          value={numberValue}
          onChange={e => onNumberChange(e.target.value)}
          placeholder="97 000 0000"
          required={required}
          disabled={geoLoading}
          className={`${inputClass} flex-1 ${geoLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
        />
      </div>
      {helperText && (
        <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1.5 mt-0.5">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.09 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 .99h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
          </svg>
          {helperText}
        </p>
      )}
    </div>
  )
}

function StepBar({ current, total }) {
  return (
    <div className="flex items-center gap-1.5 mb-8">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-300 ${
          i < current ? 'bg-green-600' : i === current ? 'bg-green-400' : 'bg-gray-200 dark:bg-white/10'
        }`} />
      ))}
    </div>
  )
}

const STEPS = ['Personal', 'Contact', 'Next of Kin', 'Club']

export default function CompleteProfileForm() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { countries, detectedCode, detectedName, getLegalAge, geoLoading } = useGeoAge()

  const [step, setStep] = useState(0)
  const [clubs, setClubs] = useState([])
  const [chapters, setChapters] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [whatsappSameAsPhone, setWhatsappSameAsPhone] = useState(true)

  const [formData, setFormData] = useState({
    full_name: '',
    gender: '', date_of_birth: '', region: '',
    phone_country: '', phone_number: '',
    whatsapp_country: '', whatsapp_number: '',
    next_of_kin_name: '', next_of_kin_country: '', next_of_kin_phone: '',
    club_id: '', chapter_id: '',
  })

  useEffect(() => {
    if (user?.user_metadata?.full_name) {
      setFormData(prev => ({ ...prev, full_name: user.user_metadata.full_name }))
    }
  }, [user])

  useEffect(() => {
    if (detectedCode) {
      setFormData(prev => ({
        ...prev,
        phone_country: detectedCode,
        whatsapp_country: detectedCode,
        next_of_kin_country: detectedCode,
      }))
    }
  }, [detectedCode])

  useEffect(() => {
    supabase.from('clubs').select('id, name').then(({ data }) => { if (data) setClubs(data) })
  }, [])

  useEffect(() => {
    if (!formData.club_id) return
    supabase.from('chapters').select('id, name').eq('club_id', formData.club_id)
      .then(({ data }) => { if (data) setChapters(data) })
  }, [formData.club_id])

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
  const handleField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }))

  const getAgeError = () => {
    if (!formData.date_of_birth || !detectedCode) return null
    const dob = new Date(formData.date_of_birth)
    if (isNaN(dob.getTime())) return null
    const today = new Date()
    let age = today.getFullYear() - dob.getFullYear()
    const m = today.getMonth() - dob.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--
    const legalAge = getLegalAge(detectedCode)
    if (age < legalAge) {
      return `You must be at least ${legalAge} years old to register in ${detectedName || detectedCode}. Please contact your club for junior membership options.`
    }
    return null
  }

  const buildPhone = (countryCode, number) => {
    const row = countries.find(c => c.country_code === countryCode)
    const dial = row?.dial_code || ''
    return dial ? `${dial} ${number}`.trim() : number.trim()
  }

  const generateMembershipId = (clubName) => {
    const prefix = clubName.split(' ').map(w => w[0]).join('').toUpperCase()
    const year = new Date().getFullYear()
    const random = Math.floor(Math.random() * 9000) + 1000
    return `${prefix}-${year}-${random}`
  }

  const validateStep = () => {
    setError(null)
    if (step === 0) {
      if (!formData.full_name.trim()) { setError('Full name is required.'); return false }
      if (!formData.gender) { setError('Please select your gender.'); return false }
      if (!formData.date_of_birth) { setError('Please complete your date of birth.'); return false }
      const ageError = getAgeError()
      if (ageError) { setError(ageError); return false }
    }
    if (step === 1) {
      if (!formData.phone_country) { setError('Please select a country code.'); return false }
      if (!formData.phone_number.trim()) { setError('Phone number is required.'); return false }
      if (!whatsappSameAsPhone) {
        if (!formData.whatsapp_country) { setError('Please select a WhatsApp country code.'); return false }
        if (!formData.whatsapp_number.trim()) { setError('WhatsApp number is required.'); return false }
      }
    }
    if (step === 2) {
      if (!formData.next_of_kin_name.trim()) { setError('Next of kin name is required.'); return false }
      if (!formData.next_of_kin_country) { setError('Please select a country code for next of kin.'); return false }
      if (!formData.next_of_kin_phone.trim()) { setError('Next of kin phone number is required.'); return false }
    }
    if (step === 3) {
      if (!formData.club_id) { setError('Please select a club.'); return false }
      if (!formData.chapter_id) { setError('Please select a chapter.'); return false }
    }
    return true
  }

  const nextStep = () => { if (validateStep()) setStep(s => s + 1) }
  const prevStep = () => { setError(null); setStep(s => s - 1) }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateStep()) return
    setLoading(true)
    setError(null)

    const selectedClub = clubs.find(c => c.id === formData.club_id)
    const membershipId = generateMembershipId(selectedClub?.name || 'MWFC')
    const phoneString = buildPhone(formData.phone_country, formData.phone_number)
    const whatsappString = whatsappSameAsPhone ? phoneString : buildPhone(formData.whatsapp_country, formData.whatsapp_number)
    const nokPhone = buildPhone(formData.next_of_kin_country, formData.next_of_kin_phone)

    const { error: supporterError } = await supabase.from('supporters').insert({
      id: user.id, full_name: formData.full_name, email: user.email,
      phone: phoneString, whatsapp_number: whatsappString,
      next_of_kin_name: formData.next_of_kin_name, next_of_kin_phone: nokPhone,
      gender: formData.gender, date_of_birth: formData.date_of_birth,
      region: formData.region, club_id: formData.club_id,
      chapter_id: formData.chapter_id, membership_id: membershipId,
      status: 'active', role: 'supporter',
    })
    if (supporterError) { setError(supporterError.message); setLoading(false); return }

    try {
      await fetch(`${BACKEND_URL}/api/email/welcome`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fullName: formData.full_name, email: user.email, membershipId, clubName: selectedClub?.name || 'MWFC' }),
      })
    } catch (err) { console.error('Welcome email failed:', err) }

    try { await notifyClubAdmins({ clubId: formData.club_id, title: 'New Member Registered', message: `${formData.full_name} just joined as a new supporter.`, type: 'new_member', link: '/admin/members' }) }
    catch (err) { console.error('Notification failed:', err) }

    navigate('/dashboard')
  }

  const inputClass = "bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-600 w-full"
  const labelClass = "text-xs text-gray-500 uppercase tracking-widest font-semibold"
  const ageError = step === 0 ? getAgeError() : null

  return (
    <div>
      <StepBar current={step} total={STEPS.length} />

      <div className="mb-6">
        <p className="text-xs text-green-600 dark:text-green-400 uppercase tracking-widest font-semibold mb-1">
          Step {step + 1} of {STEPS.length}
        </p>
        <h3 className="text-lg font-black uppercase tracking-tight text-gray-900 dark:text-white">
          {STEPS[step]}
        </h3>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">

        {/* Step 0 — Personal */}
        {step === 0 && <>
          <div className="flex flex-col gap-2">
            <label className={labelClass}>Full Name <span className="text-red-500">*</span></label>
            <input type="text" name="full_name" value={formData.full_name} onChange={handleChange} placeholder="Your full name" className={inputClass} />
          </div>
          <div className="flex flex-col gap-2">
            <label className={labelClass}>Email Address</label>
            <div className="px-4 py-3 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/5 text-gray-500 text-sm">
              {user?.email}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label className={labelClass}>Gender <span className="text-red-500">*</span></label>
            <select name="gender" value={formData.gender} onChange={handleChange} className={inputClass}>
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <DOBPicker
            value={formData.date_of_birth}
            onChange={v => handleField('date_of_birth', v)}
            inputClass={inputClass} labelClass={labelClass} error={ageError}
          />
          <div className="flex flex-col gap-2">
            <label className={labelClass}>Region / City</label>
            <input type="text" name="region" value={formData.region} onChange={handleChange} placeholder="e.g. Mufulira" className={inputClass} />
          </div>
        </>}

        {/* Step 1 — Contact */}
        {step === 1 && <>
          <PhoneRow
            label="Phone Number" countryValue={formData.phone_country} numberValue={formData.phone_number}
            onCountryChange={v => handleField('phone_country', v)} onNumberChange={v => handleField('phone_number', v)}
            countries={countries} geoLoading={geoLoading} required
            helperText="Number must be available on WhatsApp"
            inputClass={inputClass} labelClass={labelClass}
          />
          <div className="flex items-center gap-3">
            <input type="checkbox" id="whatsapp_same" checked={whatsappSameAsPhone}
              onChange={e => setWhatsappSameAsPhone(e.target.checked)} className="w-4 h-4 accent-green-600 cursor-pointer" />
            <label htmlFor="whatsapp_same" className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none">
              My WhatsApp number is the same as my phone number
            </label>
          </div>
          {!whatsappSameAsPhone && (
            <PhoneRow
              label="WhatsApp Number" countryValue={formData.whatsapp_country} numberValue={formData.whatsapp_number}
              onCountryChange={v => handleField('whatsapp_country', v)} onNumberChange={v => handleField('whatsapp_number', v)}
              countries={countries} geoLoading={geoLoading} required
              inputClass={inputClass} labelClass={labelClass}
            />
          )}
        </>}

        {/* Step 2 — Next of Kin */}
        {step === 2 && (
          <div className="border-l-4 border-green-500 bg-green-50 dark:bg-green-900/10 p-4 flex flex-col gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-green-700 dark:text-green-400 mb-1">Emergency Contact</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                This person will be contacted in case of an emergency or welfare situation involving you.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Full Name <span className="text-red-500">*</span></label>
              <input type="text" name="next_of_kin_name" value={formData.next_of_kin_name}
                onChange={handleChange} placeholder="Next of kin's full name" className={inputClass} />
            </div>
            <PhoneRow
              label="Phone Number" countryValue={formData.next_of_kin_country} numberValue={formData.next_of_kin_phone}
              onCountryChange={v => handleField('next_of_kin_country', v)} onNumberChange={v => handleField('next_of_kin_phone', v)}
              countries={countries} geoLoading={geoLoading} required
              inputClass={inputClass} labelClass={labelClass}
            />
          </div>
        )}

        {/* Step 3 — Club */}
        {step === 3 && <>
          <div className="flex flex-col gap-2">
            <label className={labelClass}>Select Your Club <span className="text-red-500">*</span></label>
            <select name="club_id" value={formData.club_id} onChange={handleChange} className={inputClass}>
              <option value="">Select a club</option>
              {clubs.map(club => <option key={club.id} value={club.id}>{club.name}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <label className={labelClass}>Select Chapter <span className="text-red-500">*</span></label>
            <select name="chapter_id" value={formData.chapter_id} onChange={handleChange}
              disabled={!formData.club_id} className={inputClass}>
              <option value="">{formData.club_id ? 'Select a chapter' : 'Select a club first'}</option>
              {chapters.map(chapter => <option key={chapter.id} value={chapter.id}>{chapter.name}</option>)}
            </select>
          </div>
        </>}

        {/* Navigation */}
        <div className={`flex gap-3 mt-2 ${step > 0 ? 'justify-between' : 'justify-end'}`}>
          {step > 0 && (
            <button type="button" onClick={prevStep}
              className="border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-white/20 px-6 py-3 text-xs font-bold uppercase tracking-widest transition-colors">
              Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button type="button" onClick={nextStep}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-xs font-bold uppercase tracking-widest transition-colors">
              Continue
            </button>
          ) : (
            <button type="submit" disabled={loading}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-900 disabled:text-green-700 text-white px-8 py-3 text-xs font-bold uppercase tracking-widest transition-colors">
              {loading ? 'Setting up your account...' : 'Complete Registration'}
            </button>
          )}
        </div>

      </form>
    </div>
  )
}